import { Amplify } from "aws-amplify";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";

import { IndexDbStorage } from "./IndexDbStorage";

let configured: Promise<void> | undefined;

/**
 * Configure Amplify for this JS realm, at most once.
 *
 * Memoized because the last thing it does is `setKeyValueStorage`, which
 * mutates global Amplify state: concurrent callers would race to swap the token
 * provider's storage while a `fetchAuthSession()` is in flight against the
 * previous instance. It also stops `IndexDbStorage` handles accumulating —
 * each holds an open `IDBDatabase` closed only by `FinalizationRegistry` on GC,
 * and open connections block IndexedDB version upgrades.
 *
 * This matters because route loaders call it on nearly every navigation, via
 * `makeAuthenticatedHttpClient`, and those loaders run concurrently.
 *
 * Module state is per-realm, so workers still configure their own copy, which
 * is what `makeAuthenticatedHttpClient` relies on.
 */
export function configureAmplify(): Promise<void> {
  configured ??= configureOnce().catch((error: unknown) => {
    // Don't cache the failure. A blocked IndexedDB open can succeed on a later
    // attempt, and a permanently-cached rejection would take auth down for the
    // rest of the session.
    configured = undefined;
    throw error;
  });
  return configured;
}

async function configureOnce(): Promise<void> {
  // Built BEFORE `Amplify.configure`, and this ordering is load-bearing.
  //
  // `Amplify.configure` resets the Cognito token provider to its default
  // storage, which is localStorage — where this app keeps nothing. Only
  // `setKeyValueStorage` points it back at IndexedDB, where the tokens actually
  // live. Any `await` between those two statements is a window in which every
  // `fetchAuthSession` reads the wrong store and reports no session, even
  // though the customer is signed in.
  //
  // That window used to hold this IndexedDB open. `HttpClient` treats a missing
  // token as an auth failure and signs the customer out, so a query firing
  // while a loader reconfigured would end the session. Opening the database
  // first leaves `configure` and `setKeyValueStorage` adjacent and synchronous,
  // with nothing to interleave. Memoizing narrows how often this runs;
  // ordering is what removes the window from the run that remains.
  const indexDbStorage = await IndexDbStorage.build();

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: `${import.meta.env.VITE_AWS_USER_POOLS_ID}`,
        userPoolClientId: `${import.meta.env.VITE_AWS_USER_POOLS_WEB_CLIENT_ID}`,
        loginWith: {
          username: true,
          email: true,
          oauth: {
            domain: `${import.meta.env.VITE_OAUTH_COGNITO_DOMAIN}`,
            scopes: ["email", "profile", "openid"],
            redirectSignIn: [`${import.meta.env.VITE_OAUTH_REDIRECT_SIGN_IN}`],
            redirectSignOut: [
              `${import.meta.env.VITE_OAUTH_REDIRECT_SIGN_OUT}`,
            ],
            responseType: "code",
          },
        },
      },
    },
  });
  cognitoUserPoolsTokenProvider.setKeyValueStorage(indexDbStorage);
}
